import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * Single source of truth for capability checks on the client.
 *
 * The provider fetches `GET /api/staff/capabilities` once per (user, business)
 * tuple, caches the response in localStorage, and exposes a `can(cap)`
 * function plus the raw payload so components can render conditionally.
 *
 * Important: this hook is for UI gating only. The backend enforces
 * authorization on every protected route — never trust the client-side
 * payload as a security boundary.
 */
const CapabilitiesCtx = createContext({
  loading: false,
  role: null,
  capabilities: {},
  can: () => false,
  refresh: () => {},
});

const STORAGE_KEY = "prenotoo_caps_v1";

function readCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function writeCache(key, data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ key, data, t: Date.now() }));
  } catch { /* ignore */ }
}

export function CapabilitiesProvider({ children }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const lastKey = useRef(null);

  const cacheKey = useMemo(() => {
    if (!user) return null;
    return `${user.id}:${user.business_id || ""}`;
  }, [user]);

  const fetchCaps = useCallback(async (overrideBid) => {
    if (!user) { setPayload(null); return; }
    const bid = overrideBid || user.business_id || null;
    setLoading(true);
    try {
      const { data } = await api.get("/staff/capabilities", {
        params: bid ? { business_id: bid } : undefined,
      });
      setPayload(data);
      // Cache key uses the resolved bid so manager/staff and owner share a
      // consistent suffix once /my/business has loaded.
      const ck = `${user.id}:${data?.business_id || bid || ""}`;
      writeCache(ck, data);
    } catch {
      setPayload({ role: null, capabilities: {}, all: [] });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setPayload(null);
      lastKey.current = null;
      return;
    }
    if (lastKey.current === cacheKey && payload) return;
    lastKey.current = cacheKey;
    // Try cache first for instant render. Owners don't carry business_id on
    // their user record, so we also accept any cached entry that starts with
    // `<userid>:` to avoid a flash of empty tabs on reload.
    const cached = readCache();
    if (cached?.data && (cached.key === cacheKey || (typeof cached.key === "string" && cached.key.startsWith(`${user.id}:`)))) {
      setPayload(cached.data);
    }
    fetchCaps();
  }, [user, cacheKey, fetchCaps, payload]);

  const can = useCallback((cap) => {
    if (!payload) return false;
    return Boolean(payload.capabilities?.[cap]);
  }, [payload]);

  const value = useMemo(() => ({
    loading,
    role: payload?.role || null,
    capabilities: payload?.capabilities || {},
    all: payload?.all || [],
    can,
    refresh: fetchCaps,
  }), [loading, payload, can, fetchCaps]);

  return <CapabilitiesCtx.Provider value={value}>{children}</CapabilitiesCtx.Provider>;
}

export function useCapabilities() {
  return useContext(CapabilitiesCtx);
}
